import * as Sentry from '@sentry/react';
import {
  attemptChunkRecoveryReload,
  hasRecentChunkRecoveryReloadAttempt,
  isChunkLoadError,
} from './chunk-recovery';

let initialized = false;

type SentryEventWithException = {
  message?: string;
  exception?: {
    values?: Array<{
      type?: string;
      value?: string;
    }>;
  };
};

const getChunkErrorCandidates = (
  event: SentryEventWithException | null | undefined,
  originalException: unknown,
): unknown[] => {
  const candidates: unknown[] = [originalException, event?.message];

  for (const exception of event?.exception?.values ?? []) {
    candidates.push(exception.value);
    candidates.push(`${exception.type ?? ''}: ${exception.value ?? ''}`);
  }

  return candidates;
};

export const shouldDropRecoveredChunkLoadEvent = (
  event: SentryEventWithException | null | undefined,
  originalException: unknown,
  recover = attemptChunkRecoveryReload,
  hasRecentRecoveryAttempt = hasRecentChunkRecoveryReloadAttempt,
): boolean => {
  const isRecoverableChunkError = getChunkErrorCandidates(event, originalException)
    .some((candidate) => isChunkLoadError(candidate));

  if (!isRecoverableChunkError) {
    return false;
  }

  return recover() || hasRecentRecoveryAttempt();
};

export function initSentry() {
  if (initialized) return;
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || 0),
    beforeSend(event, hint) {
      if (shouldDropRecoveredChunkLoadEvent(event, hint.originalException)) {
        return null;
      }

      return event;
    },
  });

  initialized = true;
}

export { Sentry };
